export const AF_CHANNELS = [
  'inapp_display', 'inapp_banner', 'banner', 'inapp', 'display',
  'banner320X50', 'banner300X250', 'in-app', 'mobile_banner',
  'mobile_display', 'InApp_Display',
]

export const DEFAULT_RETARGETING = {
  af_reengagement_window: '30d',
  af_inactivity_window: '10d',
}

export const DEFAULT_ADVANCED = {
  af_siteid: '{pid}',
  af_c_id: '{offer_id}',
  af_click_lookback: '7d',
  af_viewthrough_lookback: '24h',
  clickid: '{clickid}',
  af_sub_siteid: '{sub15}{sub6}{sub20}',
  af_ad_type: 'banner',
  af_media_type: 'app',
  af_model: '{device_model}',
  af_cost_currency: 'usd',
  af_ua: '{device_ua}',
  af_ip: '{ip}',
  device_id: '{sub5}',
  af_os_version: '{os_version}',
  af_language: '{language}',
  af_ad_format: 'image',
  af_channel: '',
}

export const pickChannel = () => AF_CHANNELS[Math.floor(Math.random() * AF_CHANNELS.length)]

export const dateSuffix = () => {
  const now = new Date()
  const dd = String(now.getDate()).padStart(2, '0')
  const mm = String(now.getMonth() + 1).padStart(2, '0')
  const r = Math.floor(Math.random() * 10)
  return `${dd}${mm}${r}`
}

export const buildAdsetAd = (codes) => {
  const pool = [...codes]
  const i = Math.floor(Math.random() * pool.length)
  const adsetCode = pool.splice(i, 1)[0]
  const adCode = pool[Math.floor(Math.random() * pool.length)]
  const suffix = dateSuffix()
  return { adset: `${adsetCode}${suffix}`, ad: `${adCode}${suffix}` }
}

export const validateBundle = (os, raw) => {
  const v = raw.trim()
  if (!v) return 'Bundle ID is required.'
  if (os === 'ios') {
    if (!/^\d+$/.test(v)) return 'iOS expects the numeric App Store ID (digits only), e.g. 284882215.'
  } else if (!/^[a-zA-Z][a-zA-Z0-9_]*(\.[a-zA-Z0-9_]+)+$/.test(v)) {
    return 'Android expects a package name, e.g. com.company.app.'
  }
  return ''
}

export const partnerSubFields = (partner) => {
  if (!partner) return null
  const fields = {
    af_adset_id: String(partner.af_adset_id || '').trim(),
    af_adset: String(partner.af_adset || '').trim(),
    af_ad: String(partner.af_ad || '').trim(),
    af_ad_id: String(partner.af_ad_id || '').trim(),
  }
  const complete = Object.values(fields).every(Boolean)
  return complete ? fields : null
}

const pushParam = (p, key, value) => {
  const v = value == null ? '' : String(value).trim()
  if (v) p.push(`${key}=${v}`)
}

export const stripWhitespaceIfPresent = (raw) => {
  const text = raw == null ? '' : String(raw)
  if (!/\s/.test(text)) return { value: text, stripped: false }
  return { value: text.replace(/\s+/g, ''), stripped: true }
}

export const sanitizeManualOrigin = (raw) => {
  let s = String(raw == null ? '' : raw).replace(/\s+/g, '')
  s = s.replace(/^https?:\/\//i, '')
  const q = s.indexOf('?')
  if (q !== -1) s = s.slice(0, q)
  s = s.replace(/\?/g, '').replace(/\/+$/, '')
  return s
}

export const extractManualOriginFromUrl = (url) => {
  const raw = String(url || '').trim()
  if (!raw) return ''
  try {
    const u = new URL(raw)
    return sanitizeManualOrigin(u.host + u.pathname)
  } catch (e) {
    return sanitizeManualOrigin(raw)
  }
}

export const applyOriginOverride = (url, originOverride) => {
  const hostPath = sanitizeManualOrigin(originOverride)
  if (!hostPath || !url) return url || ''
  let search = ''
  let hash = ''
  try {
    const u = new URL(url)
    search = u.search
    hash = u.hash
  } catch (e) {
    const raw = String(url)
    const hashIdx = raw.indexOf('#')
    if (hashIdx !== -1) hash = raw.slice(hashIdx)
    const end = hashIdx === -1 ? raw.length : hashIdx
    const qIdx = raw.indexOf('?')
    if (qIdx !== -1 && qIdx < end) search = raw.slice(qIdx, end)
  }
  return 'https://' + hostPath + search + hash
}

export const applyOriginOverrideToLinks = (links, originOverride) => {
  const hostPath = sanitizeManualOrigin(originOverride)
  if (!hostPath) return links || []
  return (links || []).map((l) => ({
    ...l,
    click: l && l.click ? applyOriginOverride(l.click, hostPath) : (l && l.click) || '',
    impression: l && l.impression ? applyOriginOverride(l.impression, hostPath) : (l && l.impression) || null,
  }))
}

export const buildUrl = ({
  os,
  bundleId,
  agency,
  pid,
  channel,
  campaignName,
  costModel,
  isRetargeting,
  additionalParams,
  subFields = null,
  adset,
  ad,
  isVTA,
  advanced = {},
  reengagementWindow,
  inactivityWindow,
  originOverride,
}) => {
  const host = isVTA ? 'https://impression.appsflyer.com/' : 'https://app.appsflyer.com/'
  const appPart = os === 'ios' ? 'id' + bundleId.trim() : bundleId.trim()
  const a = { ...DEFAULT_ADVANCED, ...advanced }

  const p = []
  p.push(`pid=${encodeURIComponent(pid)}`)
  if (agency.trim()) p.push(`af_prt=${encodeURIComponent(agency.trim())}`)
  pushParam(p, 'af_siteid', DEFAULT_ADVANCED.af_siteid)
  pushParam(p, 'af_c_id', DEFAULT_ADVANCED.af_c_id)
  if (isVTA) {
    pushParam(p, 'af_viewthrough_lookback', a.af_viewthrough_lookback)
  } else {
    pushParam(p, 'af_click_lookback', a.af_click_lookback)
  }
  pushParam(p, 'clickid', DEFAULT_ADVANCED.clickid)
  p.push(`c=${encodeURIComponent(campaignName.trim())}`)
  pushParam(p, 'af_sub_siteid', a.af_sub_siteid)
  pushParam(p, 'af_channel', channel)
  pushParam(p, 'af_ad_type', a.af_ad_type)
  if (subFields) {
    pushParam(p, 'af_adset', subFields.af_adset)
    pushParam(p, 'af_adset_id', subFields.af_adset_id)
    pushParam(p, 'af_ad', subFields.af_ad)
    pushParam(p, 'af_ad_id', subFields.af_ad_id)
  } else {
    p.push(`af_adset=${adset}{sub7}`)
    p.push(`af_adset_id=${adset}`)
    p.push(`af_ad=${ad}{sub2}`)
    p.push(`af_ad_id=${ad}`)
  }
  pushParam(p, 'af_media_type', a.af_media_type)
  pushParam(p, 'af_model', a.af_model)
  pushParam(p, 'af_cost_currency', a.af_cost_currency)
  p.push(`af_cost_model=${costModel}`)
  pushParam(p, 'af_ua', a.af_ua)
  pushParam(p, 'af_ip', a.af_ip)
  pushParam(p, os === 'android' ? 'advertising_id' : 'idfa', a.device_id)
  pushParam(p, 'af_os_version', a.af_os_version)
  pushParam(p, 'af_language', a.af_language)
  p.push(`is_retargeting=${isRetargeting === 'yes' ? 'true' : 'false'}`)
  if (isRetargeting === 'yes') {
    pushParam(p, 'af_reengagement_window', reengagementWindow)
    pushParam(p, 'af_inactivity_window', inactivityWindow)
  }
  pushParam(p, 'af_ad_format', a.af_ad_format)

  let url = `${host}${appPart}?${p.join('&')}`
  const extra = additionalParams.trim().replace(/^[?&\s]+/, '')
  if (extra) url += '&' + extra
  return applyOriginOverride(url, originOverride)
}
